import { Zap, Sparkles, BookOpen, Activity, MessageSquare, Heart, Eye, Search, TrendingUp, Target, Coins, BarChart3 } from "lucide-react";

export const scoreTone = (v) => {
  const n = Number(v) || 0;
  if (n >= 70) return { text: "text-emerald-300", bar: "from-purple-500 to-cyan-400", ring: "#06B6D4", glow: "rgba(6,182,212,0.35)" };
  if (n >= 40) return { text: "text-amber-300", bar: "from-amber-400 to-orange-500", ring: "#F59E0B", glow: "rgba(245,158,11,0.35)" };
  return { text: "text-rose-300", bar: "from-rose-500 to-pink-500", ring: "#F43F5E", glow: "rgba(244,63,94,0.35)" };
};

export const SectionHeading = ({ eyebrow, title, icon: Icon, accent = "violet" }) => {
  const c = { violet: "text-purple-300 bg-purple-500/10 border-purple-400/30", cyan: "text-cyan-300 bg-cyan-500/10 border-cyan-400/30", magenta: "text-pink-300 bg-pink-500/10 border-pink-400/30" }[accent];
  return (
    <div className="flex items-center gap-3 pt-4">
      {Icon && (
        <span className={`w-9 h-9 rounded-xl border flex items-center justify-center ${c}`}>
          <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" strokeWidth={1.75} />
        </span>
      )}
      <div>
        {eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-muted">{eyebrow}</p>}
        <h3 className="text-lg md:text-xl font-bold text-gray-900">{title}</h3>
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-brand-red/50 via-purple-500/25 to-transparent ml-2" />
    </div>
  );
};

export const RadialScore = ({ value, size = 200, stroke = 12, label, sublabel, id = "score", compact = false }) => {
  const n = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (n / 100) * c;
  return (
    <div className="relative flex flex-col items-center justify-center" style={{ width: size, height: size }}>
      <div className="absolute inset-4 rounded-full bg-purple-600/25 blur-2xl" aria-hidden="true" />
      <svg width={size} height={size} className="-rotate-90 relative">
        <defs>
          <linearGradient id={`grad-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="55%" stopColor="#EC4899" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={`url(#grad-${id})`} strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`}
          style={{ filter: "drop-shadow(0 0 8px rgba(139,92,246,0.55))", transition: "stroke-dasharray 1s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span data-testid={`radial-${id}-value`} className={`vt-score font-extrabold text-vt-ink leading-none ${compact ? "text-2xl" : "text-5xl md:text-6xl"}`}>{n}</span>
        {!compact && <span className="text-sm text-brand-muted font-semibold mt-1">/100</span>}
        {label && <span className="mt-2 text-xs font-bold uppercase tracking-widest text-purple-300">{label}</span>}
        {sublabel && <span className="text-[11px] text-brand-text2/80">{sublabel}</span>}
      </div>
    </div>
  );
};

const METRIC_META = {
  hook: { label: "Hook", icon: Zap }, curiosity: { label: "Curiosidad", icon: Sparkles }, storytelling: { label: "Storytelling", icon: BookOpen },
  rhythm: { label: "Ritmo", icon: Activity }, cta: { label: "CTA", icon: MessageSquare }, emotion: { label: "Emoción", icon: Heart },
  retention: { label: "Retención", icon: Eye }, seo: { label: "SEO", icon: Search }, structure: { label: "Estructura", icon: BarChart3 },
  value: { label: "Valor", icon: Target }, monetization: { label: "Monetización", icon: Coins }, growth: { label: "Crecimiento", icon: TrendingUp },
};

export const MetricCard = ({ name, value, index = 0 }) => {
  const meta = METRIC_META[name] || { label: name, icon: BarChart3 };
  const Icon = meta.icon;
  const tone = scoreTone(value);
  const accents = ["text-purple-300 border-purple-400/30 bg-purple-500/10", "text-cyan-300 border-cyan-400/30 bg-cyan-500/10", "text-pink-300 border-pink-400/30 bg-pink-500/10"];
  return (
    <div data-testid={`metric-card-${name}`} className="vt-card p-4 bg-brand-card">
      <div className="flex items-center justify-between mb-3">
        <span className={`w-9 h-9 rounded-xl border flex items-center justify-center ${accents[index % 3]}`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </span>
        <div className="text-right leading-none">
          <span className={`vt-score text-2xl font-extrabold ${tone.text}`}>{value}</span>
          <span className="text-xs text-brand-muted font-semibold">/100</span>
        </div>
      </div>
      <p className="text-sm font-semibold text-brand-text2 capitalize mb-2">{meta.label}</p>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${tone.bar}`} style={{ width: `${Math.max(0, Math.min(100, Number(value) || 0))}%`, boxShadow: `0 0 10px ${tone.glow}` }} />
      </div>
    </div>
  );
};
